import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CheckoutPageId } from 'checkout/constants/pages'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

export const FLIGHTS_STEPPED_CHECKOUT_STEP_LABEL = {
  review: 'flights-stepped-checkout-step-review',
  payment: 'flights-stepped-checkout-step-payment',
} as const

export const FlightChangeDatesPageStep = {
  SELECT_FLIGHTS_TO_CHANGE: 'SELECT_FLIGHTS_TO_CHANGE',
  SELECT_DATES_TO_CHANGE: 'SELECT_DATES_TO_CHANGE',
  SELECT_NEW_FLIGHTS: 'SELECT_NEW_FLIGHTS',
  INSURANCE_SELECT_DATES: 'INSURANCE_SELECT_DATES',
  REVIEW_AND_PAY: 'REVIEW_AND_PAY',
} as const

export type FlightChangeDatesPageStepDefinition = typeof FlightChangeDatesPageStep[keyof typeof FlightChangeDatesPageStep]

export function useFlightsCheckoutStepLabels(): Record<string, string> {
  const intl = useIntl()
  return useMemo(() => ({
    'Change Flights': intl.formatMessage({
      id: 'mCUiaCXjdNYWrYriYgOy',
      defaultMessage: 'Change Flights',
    }),
    'Select dates': intl.formatMessage({
      id: 'AJMfbR0al_aD4sJ_WoUq',
      defaultMessage: 'Select dates',
    }),
    'New Flights': intl.formatMessage({
      id: 'A1OTmboPUI6Endx91eCA',
      defaultMessage: 'New Flights',
    }),
    Insurance: intl.formatMessage({
      id: '5_LkrB92C3hpGZX-AxaG',
      defaultMessage: 'Insurance',
    }),
    'Review & Pay': intl.formatMessage({
      id: 'rci-rfqPUosbRRXcXri1',
      defaultMessage: 'Review & Pay',
    }),
    [FLIGHTS_STEPPED_CHECKOUT_STEP_LABEL.review]: intl.formatMessage({
      id: '8rfCzQcfYjwcBqUDmnAZ',
      defaultMessage: 'Your flights',
    }),
    [FLIGHTS_STEPPED_CHECKOUT_STEP_LABEL.payment]: intl.formatMessage({
      id: 'uuOeEEyIzaIeapnestbc',
      defaultMessage: 'Checkout',
    }),
  }), [intl])
}

export const FLIGHTS_CHECKOUT_HEADER_STEPS: Array<App.Checkout.CheckoutStep> = [
  {
    label: 'Change Flights',
    key: FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE,
    subSteps: [],
    isRoute: false,
  },
  {
    label: 'Select dates',
    key: FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE,
    subSteps: [],
    isRoute: true,
  },
  {
    label: 'New Flights',
    key: FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS,
    isRoute: true,
    subSteps: [],
  },
  {
    label: 'Insurance',
    key: FlightChangeDatesPageStep.INSURANCE_SELECT_DATES,
    isRoute: true,
    subSteps: [],
  },
  {
    label: 'Review & Pay',
    key: FlightChangeDatesPageStep.REVIEW_AND_PAY,
    isRoute: true,
    subSteps: [],
  },
]

export const FLIGHTS_STEPPED_CHECKOUT_HEADER_STEPS: Record<
  'luxPlus' | 'experiences' | 'review' | 'payment',
  App.Checkout.CheckoutStep
> = {
  luxPlus: {
    label: LUXURY_PLUS.PROGRAM_NAME,
    key: CheckoutPageId.LuxPlus,
    subSteps: [],
    isRoute: true,
  },
  experiences: {
    label: 'Experiences',
    key: CheckoutPageId.Experiences,
    subSteps: [],
    isRoute: true,
  },
  review: {
    label: FLIGHTS_STEPPED_CHECKOUT_STEP_LABEL.review,
    key: CheckoutPageId.Review,
    subSteps: [],
    isRoute: true,
  },
  payment: {
    label: FLIGHTS_STEPPED_CHECKOUT_STEP_LABEL.payment,
    key: CheckoutPageId.Payment,
    subSteps: [],
    isRoute: true,
  },
}

interface BuildFlightsSteppedCheckoutStepsOptions {
  currentRoute?: string
  isLuxPlusStepAvailable: boolean
  isExperiencesStepAvailable: boolean
  hasSubscriptionJoinItems: boolean
  hasExperienceItems: boolean
}

/**
 * Experiences before LuxPlus, the reverse of every other funnel.
 * `getNextCheckoutPage` defers LuxPlus to match — the two must agree or the
 * stepper highlights the wrong step.
 */
export function buildFlightsSteppedCheckoutSteps({
  currentRoute,
  isLuxPlusStepAvailable,
  isExperiencesStepAvailable,
  hasSubscriptionJoinItems,
  hasExperienceItems,
}: BuildFlightsSteppedCheckoutStepsOptions): Array<App.Checkout.CheckoutStep> {
  const steps: Array<App.Checkout.CheckoutStep> = []

  if (isExperiencesStepAvailable || currentRoute === CheckoutPageId.Experiences || hasExperienceItems) {
    steps.push(FLIGHTS_STEPPED_CHECKOUT_HEADER_STEPS.experiences)
  }

  if (isLuxPlusStepAvailable || currentRoute === CheckoutPageId.LuxPlus || hasSubscriptionJoinItems) {
    steps.push(FLIGHTS_STEPPED_CHECKOUT_HEADER_STEPS.luxPlus)
  }

  steps.push(FLIGHTS_STEPPED_CHECKOUT_HEADER_STEPS.review)
  steps.push(FLIGHTS_STEPPED_CHECKOUT_HEADER_STEPS.payment)

  return steps
}
